import { useToast } from "@/store";
import {
  getNextThemePreference,
  getSystemColorScheme,
  persistThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "@/utils/theme";
import { captureException } from "@sentry/vue";
import { computed } from "vue";
import { useTheme } from "vuetify";

export const resolvedThemeIcons = {
  light: "mdi-white-balance-sunny",
  dark: "mdi-weather-night",
} as const satisfies Record<ResolvedTheme, string>;

export function useThemePreference() {
  const theme = useTheme();
  const { showErrorToast } = useToast();
  let changeToken = 0;

  const preference = computed<ThemePreference>(() => {
    if (theme.isSystem.value) return "system";
    const name = theme.name.value;
    if (name === "light" || name === "dark") return name;
    return "system";
  });

  // theme.name resolves "system" to the active light/dark appearance.
  const resolved = computed<ResolvedTheme>(() => (theme.name.value === "dark" ? "dark" : "light"));

  const icon = computed(() => resolvedThemeIcons[resolved.value]);

  const applyTheme = async (value: ThemePreference) => {
    const token = ++changeToken;
    try {
      await theme.change(value);
      if (token !== changeToken) return;
      persistThemePreference(value);
    } catch (cause) {
      if (token !== changeToken) return;
      const error = new Error(`Error changing theme to ${value}`, { cause });
      console.error(error);
      captureException(error);
      showErrorToast();
    }
  };

  const toggleTheme = async () => {
    await applyTheme(
      getNextThemePreference({
        resolved: resolved.value,
        system: getSystemColorScheme(),
      }),
    );
  };

  return {
    preference,
    resolved,
    icon,
    toggleTheme,
  };
}
